import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mentionsStillPresent, type MentionRefDto } from '@munifocus/contracts-gov';
import type { MentionSuggestion } from '@munifocus/ui';
import { useMentionable } from '../../lib/teamwork-api';

/**
 * The @mention picker's state for one file: who can be mentioned (only people
 * who can read the file — the server answers that), who has been picked, and
 * which of those are still in the text when it is sent.
 */
export function useFileMentions(fileId: string, enabled = true) {
  const { t } = useTranslation();
  const people = useMentionable(fileId, enabled);
  const [picked, setPicked] = useState<MentionRefDto[]>([]);

  const suggestions: MentionSuggestion[] = useMemo(
    () =>
      (people.data ?? []).map((person) => ({
        id: person.userId,
        label: person.displayName,
        hint: person.roles
          .map((role) => t(`gov.settings.role.${role}`, { defaultValue: role }))
          .join(', '),
      })),
    [people.data, t],
  );

  const onPick = useCallback((s: MentionSuggestion) => {
    setPicked((prev) =>
      prev.some((p) => p.userId === s.id)
        ? prev
        : [...prev, { userId: s.id, displayName: s.label }],
    );
  }, []);

  /** The ids to send with `text`: picked people whose "@Name" is still in it. */
  const mentionIdsFor = useCallback(
    (text: string) => mentionsStillPresent(text, picked).map((m) => m.userId),
    [picked],
  );

  const reset = useCallback((initial: MentionRefDto[] = []) => setPicked(initial), []);

  return { suggestions, onPick, mentionIdsFor, reset, picked };
}
